import { CompactCalculator } from './calculator.compact';
import { createElement, ElementInfo } from './dom-util';
import { Mode } from './constants';
import CheckoutUtility, { config } from './shopify-checkout-util';
import { SKU } from './utils/sku';

const questionMark = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="1" y="1" width="22" height="22" rx="11" fill="white"/>
<rect x="1" y="1" width="22" height="22" rx="11" stroke="currentColor" stroke-width="2"/>
<path d="M10.5227 14.0795V13.9318C10.5265 13.2462 10.5909 12.7008 10.7159 12.2955C10.8447 11.8902 11.0265 11.5644 11.2614 11.3182C11.4962 11.0682 11.7822 10.839 12.1193 10.6307C12.3504 10.4867 12.5568 10.3295 12.7386 10.1591C12.9242 9.98485 13.0701 9.79167 13.1761 9.57955C13.2822 9.36364 13.3352 9.12311 13.3352 8.85795C13.3352 8.55871 13.2652 8.29924 13.125 8.07955C12.9848 7.85985 12.7955 7.68939 12.5568 7.56818C12.322 7.44697 12.0587 7.38636 11.767 7.38636C11.4981 7.38636 11.2424 7.44508 11 7.5625C10.7614 7.67614 10.5625 7.85038 10.4034 8.08523C10.2481 8.31629 10.161 8.60985 10.142 8.96591H8.11932C8.13826 8.24621 8.3125 7.64394 8.64205 7.15909C8.97538 6.67424 9.41477 6.31061 9.96023 6.06818C10.5095 5.82576 11.1155 5.70455 11.7784 5.70455C12.5019 5.70455 13.1383 5.83144 13.6875 6.08523C14.2405 6.33902 14.6705 6.70076 14.9773 7.17045C15.2879 7.63636 15.4432 8.18939 15.4432 8.82955C15.4432 9.26136 15.3731 9.64773 15.233 9.98864C15.0966 10.3295 14.9015 10.6326 14.6477 10.8977C14.3939 11.1629 14.0928 11.3996 13.7443 11.608C13.4375 11.7973 13.1856 11.9943 12.9886 12.1989C12.7955 12.4034 12.6515 12.6439 12.5568 12.9205C12.4659 13.1932 12.4186 13.5303 12.4148 13.9318V14.0795H10.5227ZM11.5114 17.625C11.1705 17.625 10.8769 17.5038 10.6307 17.2614C10.3845 17.0189 10.2614 16.7235 10.2614 16.375C10.2614 16.0341 10.3845 15.7424 10.6307 15.5C10.8769 15.2576 11.1705 15.1364 11.5114 15.1364C11.8485 15.1364 12.1402 15.2576 12.3864 15.5C12.6364 15.7424 12.7614 16.0341 12.7614 16.375C12.7614 16.6061 12.7027 16.8163 12.5852 17.0057C12.4716 17.1951 12.3201 17.3466 12.1307 17.4602C11.9451 17.5701 11.7386 17.625 11.5114 17.625Z" fill="black"/>
</svg>
`;

type SHOP_CODE = 'ex' | 'mi' | 'vt' | 'ap';

export default class CompactCheckoutUtility extends CheckoutUtility {
  constructor(
    code: string,
    mode: Mode,
    stage: keyof typeof config,
    shopCode: string,
    currencyCode = 'USD',
    collection?: string,
    disableDesignService?: boolean,
    enablePublicModal?: boolean,
    soCollectionHintText?: string,
  ) {
    const splittedCode = code?.split(':::');
    //'CHECKERED_KIT:::PS:CHECKERED-CT:TOKYO'
    const defaultPatternSet =
      splittedCode?.length > 1 ? +splittedCode[1]?.split('-')[0]?.split(':')[1] : undefined;
    const defaultColorTheme =
      splittedCode?.length > 1 ? +splittedCode[1]?.split('-')[1]?.split(':')[1] : undefined;

    const prdWithSizeSpecs: { [productCode in string]: string } = {};
    splittedCode[1]
      ?.split('-')
      .slice(2)
      .map((code) => {
        const [productCode, sizeSpecValue] = code.split(':');
        prdWithSizeSpecs[productCode] = sizeSpecValue;
      });
    super(
      splittedCode[0],
      mode,
      stage,
      shopCode,
      new CompactCalculator(splittedCode[0], mode, {
        calculatorAPIUrl: config[stage][shopCode as SHOP_CODE].calculatorAPIUrl,
        apiUrl: config[stage][shopCode as SHOP_CODE].apiUrl,
        adminUrl: config[stage].adminUrl,
        shopCode: shopCode,
        enablePublicModal: enablePublicModal,
        stage,
        defaultPatternSet,
        defaultColorTheme,
        defaultPrdSizeSpec: prdWithSizeSpecs,
        isDesignServiceDisabled: disableDesignService,
        soCollectionHintText: soCollectionHintText,
      }),
      enablePublicModal,
    );
    if (collection) {
      const collectionItems = this.getCollectionItems(collection);
      this.calculator.stateMng.subscribe('contentLoaded', () => {
        collectionItems.then(this.__renderCollectionItems.bind(this));
      });
    }
    if (currencyCode !== 'USD') {
      const currency = this.getCurrency(currencyCode);
      currency.then((data) => {
        if (data) {
          this.calculator.stateMng.set('currency', {
            code: data.code,
            rate: data.product_exchange_rate,
            symbol: data.symbol,
          });
        } else {
          console.warn(`Currency with code ${currencyCode} not found.`);
        }
      });
    }
  }

  getCurrency(currencyCode: string) {
    return fetch(`${this.config[this.shopCode as SHOP_CODE].apiUrl}/currency/${currencyCode}`).then(
      (res) => (res.status === 200 ? res.json() : null),
    );
  }

  getCollectionItems(collection: string) {
    const shopDomain = window.location.hostname;
    return fetch(
      `${this.config[this.shopCode as SHOP_CODE].calculatorAPIUrl}/rest/shopify/collections/${collection}/products?shopDomain=${shopDomain}`,
    ).then((res) => (res.status === 200 ? res.json() : null));
  }

  protected handleShowDes(e: Event) {
    const el = e.target as HTMLElement;
    if ('target' in el.dataset) {
      const targetKey = el.dataset['target'];
      if (targetKey) {
        const modal = document.getElementById(targetKey);
        const questionMark = document.getElementById(targetKey + '__question');
        if (modal) {
          modal.style.display = 'block';
          if (questionMark) questionMark.style.display = 'flex';
          document.addEventListener('click', this.handleOutsideClick.bind(this, modal), true);
        }
      }
    }
  }

  protected handleOutsideClick(modal: HTMLElement, e: Event) {
    if (!modal.contains(e.target as Node)) {
      const questionMark = document.getElementById(modal.id + '__question');
      modal.style.display = 'none';
      if (questionMark) questionMark.style.display = '';
      document.removeEventListener('click', this.handleOutsideClick.bind(this, modal), true);
    }
  }

  __renderCollectionItems(collectionItems: any) {
    if (!(collectionItems instanceof Array)) {
      console.debug('Collection items must be an array');
      return;
    }

    const firstSpecContent = document.querySelector('.calc-spec__content');
    if (!firstSpecContent) {
      console.warn('No container found for collection items');
      return;
    }

    function onSelectChange(e: Event) {
      const select = e.target as HTMLSelectElement;
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('sku');
      currentUrl.pathname = '/products/' + select.value;
      window.location.href = currentUrl.toString();
    }

    const specsDescriptions: ElementInfo = {
      tag: 'div',
      attributes: {
        class: 'calc-spec__content__item__descriptions__modal',
        id: `so-collection-hint__descriptions__modal`,
        style: 'display:none',
      },
      children: [
        {
          tag: 'div',
          attributes: {
            class: 'calc-spec__content__item__descriptions__modal__content',
            style:
              'background-color: #F9F6F5;border-bottom-right-radius: 0;border-bottom-left-radius: 0;border-bottom: 1px solid var(--Black-8, #E6E6E6); ',
          },
          children: [
            {
              tag: 'p',
              attributes: {
                class: 'detail',
              },
              children: [this.calculator.stateMng.get('soCollectionHintText') ?? ''],
            },
          ],
        },
      ],
    };
    const specsQuestionMark = {
      tag: 'div',
      attributes: {
        class: 'calc-spec__content__item__question',
        id: `so-collection-hint__descriptions__modal__question`,
      },
      children: [
        {
          tag: 'img',
          attributes: {
            style: 'color: var(--primary-color);',
            src: URL.createObjectURL(new Blob([questionMark], { type: 'image/svg+xml' })),
            alt: 'Question Mark',
            'data-target': `so-collection-hint__descriptions__modal`,
          },
        },
      ],
      events: { click: this.handleShowDes.bind(this) },
    };

    const hintText = this.calculator.stateMng.get('soCollectionHintText') ?? '';
    const hint = hintText !== '' ? [specsDescriptions, specsQuestionMark] : [];

    const selector = createElement({
      tag: 'div',
      // data-ignore: true is used to ignore calc change events on this element
      attributes: { class: 'calc-spec__content__item', 'data-ignore': true },
      children: [
        {
          tag: 'select',
          attributes: { class: 'calc-spec__collection-select' },
          events: { change: onSelectChange },
          children: collectionItems.map((item: any) => ({
            tag: 'option',
            attributes: {
              'data-code': item.sku,
              value: item.handle,
              selected: this.code === item.sku ? '' : undefined,
            },
            children: [item?.meta?.find((m: any) => m.key === 'so_title')?.value ?? item.title],
          })),
        },
        ...hint,
      ],
    });

    const chips = createElement({
      tag: 'div',
      attributes: { class: 'calc-spec__content__item' },
      children: [
        {
          tag: 'div',
          attributes: { class: 'calc-spec__content__item_specs' },
          children: collectionItems.map((item: any) => ({
            tag: 'label',
            attributes: {
              class: 'calc-spec__collection-select__item',
              'data-code': item.sku,
            },
            children: [
              {
                tag: 'input',
                attributes: {
                  type: 'radio',
                  name: 'collection',
                  value: item.handle,
                  checked: this.code === item.sku ? 'true' : undefined,
                },
                events: { change: onSelectChange },
              },
              {
                tag: 'button',
                children: [item?.meta?.find((m: any) => m.key === 'so_title')?.value ?? item.title],
              },
            ],
          })),
        },
        ...hint,
      ],
    });

    let itemsLength = 0;

    collectionItems?.forEach((item) => {
      const title = item?.meta?.find((m: any) => m.key === 'so_title')?.value ?? item.title;
      itemsLength += title.length;
    });

    if (itemsLength > 50) firstSpecContent.prepend(selector);
    else firstSpecContent.prepend(chips);
  }
}

if (typeof window !== 'undefined') {
  window.CheckoutUtility = CompactCheckoutUtility;
}
